import type {TimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';

export enum TimelineItemType {
  AUDIO = 'audio',
  ITEM = 'item',
  BACKGROUND = 'background',
  SUBTITLE = 'subtitle',
  TRANSITION = 'transition',
}

/**
 * Represents a selected timeline item.
 * Uses discriminated union pattern to ensure only one item is selected at a time.
 * Easily extensible for new timeline item types.
 */
export interface SelectedTimelineItem {
  type: TimelineItemType;
  id: string;
}

export interface TimelineItemData {
  id: string;
  type: TimelineItemType;
  startTime: number;
  endTime: number;
  subRow: number;
}

/**
 * Discriminated union for the action being performed on a timeline item.
 * - 'dragging': Item is being moved horizontally
 * - 'resizing': Item duration is being changed via resize handles
 */
export type TimelineModifyingAction = {type: 'dragging'} | {type: 'resizing'; resizeHandle: 'left' | 'right'};

/** Identity of one timeline bar across the multi-selectable tracks. */
export interface TimelineBarRef {
  id: string;
  type: TimelineItemType;
}

/** The bars a selection extension (shift-click) resolved to, split by owning track. */
export interface TimelineSelectionExtensionUids {
  itemUids: string[];
  audioUids: string[];
}

/**
 * One bar of a multi-bar gesture, snapshotted when the gesture starts — the
 * `original*` fields are FROZEN gesture-start values, never live ones. Everything the
 * co-preview and the commit need — identity, original span/row, resize bounds — is
 * captured here, so both are pure functions of this snapshot plus the live delta:
 * preview ≡ commit by construction, and selection churn mid-gesture cannot corrupt
 * the gesture.
 */
export interface TimelineGroupGestureMember extends TimelineBarRef {
  originalStartTime: number;
  originalEndTime: number;
  originalSubRow: number;
  resizeBounds: TimelineResizeBounds;
}

/**
 * Where the dragged bar lands if released NOW: the track under the pointer, already
 * policy-filtered — the bar's own track, or a foreign track that converts it.
 * Undefined means no valid target (pointer outside every accepting track) and the bar
 * snaps home on release. Resolved per pointermove; the drop commit reads the same
 * value, so preview and landing position agree by construction.
 */
export interface TimelinePointerDropTarget {
  trackType: TimelineItemType;
  /** Row the bar lands on, for 'rows' layouts. */
  row: number;
  /** Position in the background sequence the bar inserts at, for 'sequence' layouts. */
  insertionIndex?: number;
}

/**
 * The in-flight timeline gesture (drag or resize) — Redux is the single source of
 * truth for it so every bar can render real-time previews. Top-level fields are
 * POINTER state: there is one pointer, so one live span, one target row, one raw Y
 * offset. Bar-associated data lives in `grabbed` (which bar the pointer holds) and
 * `groupMembers` (frozen per-member snapshots for group gestures).
 */
export interface TimelineModifyingState {
  /** The bar the pointer grabbed — the one the snap engine and cursor math track. */
  grabbed: TimelineBarRef;
  /** The action being performed (dragging or resizing) */
  action: TimelineModifyingAction;
  /** LIVE start time of the grabbed bar, updated every pointermove */
  startTime: number;
  /** LIVE end time of the grabbed bar, updated every pointermove */
  endTime: number;
  /** Row the grabbed bar would land on during vertical drag */
  targetSubRow?: number;
  /** Gap index for new row creation during vertical drag */
  insertionIndex?: number;
  /** The grabbed bar's row when the gesture started (base row for deltaY math) */
  originalSubRow?: number;
  /** Raw vertical pixel offset from drag start for free Y-axis movement */
  dragDeltaY?: number;
  /** The track the bar lands on if released now — own or convert-policy foreign; undefined = snaps home. */
  dropTarget?: TimelinePointerDropTarget;
  /**
   * Frozen per-member snapshots for a multi-bar gesture (grabbed bar included).
   * Absent for single-bar gestures — presence selects the group commit path, which
   * deliberately differs from the single-bar one (no row insertion, per-bar clamps).
   */
  groupMembers?: TimelineGroupGestureMember[];
}

export const TIMELINE_ITEM_DATA_ATTRIBUTE = 'data-timeline-item';

/**
 * Elements carrying this attribute suppress the timeline's hover seek-line while the pointer
 * is over them (resize handles, boundary chips) — the line otherwise reads as a seek preview
 * on elements that don't seek.
 */
export const TIMELINE_HOVER_LINE_SUPPRESS_DATA_ATTRIBUTE = 'data-timeline-hover-line-suppress';
/**
 * The opaque control column over the timeline's left inset, and the per-row button fragments
 * riding above it. Clicks inside it operate on the rail's controls only — they must not seek,
 * clear selections, or open the timeline context menu.
 */
export const TIMELINE_CONTROL_COLUMN_DATA_ATTRIBUTE = 'data-timeline-control-column';
/** Rendered by TimelineTrack with the track's TimelineItemType — the anchor for pointer→track geometry. */
export const TIMELINE_TRACK_DATA_ATTRIBUTE = 'data-timeline-track';
export const TIMELINE_RULER_DATA_ATTRIBUTE = 'data-timeline-ruler';
export const TIMELINE_CONTAINER_DATA_ATTRIBUTE = 'data-timeline-container';
export const POSTER_WHITEBOARD_CONTAINER_ID = 'poster-whiteboard-container';

export const DEFAULT_PIXELS_PER_SECOND = 80;
export const PAGE_TRACK_HEIGHT = 64;
export const ITEM_TRACK_HEIGHT = 28;
export const SUBTITLE_TRACK_HEIGHT = ITEM_TRACK_HEIGHT;
export const TRACK_GAP = 8;
/** Floor for a bar's rendered width so a zero/near-zero-span item stays visible and grabbable on its track. */
export const MIN_RENDERED_BAR_WIDTH_PX = 8;
/** On the timeline's first open the zoom is set so the page spans this share of the container (see useInitialTimelineZoom). */
export const INITIAL_ZOOM_PAGE_VIEW_FRACTION = 0.66;
export const TIMELINE_HORIZONTAL_PADDING = 12;
/** Width of a rail button in the sticky left control column (mute/unmute). */
export const TIMELINE_RAIL_WIDTH = 32;
/** Breathing room on each side of a rail button inside the control column. */
export const TIMELINE_RAIL_BUTTON_GUTTER = 8;
/** Full width of the opaque control column: a rail button plus its side gutters. */
export const TIMELINE_CONTROL_COLUMN_WIDTH = TIMELINE_RAIL_WIDTH + 2 * TIMELINE_RAIL_BUTTON_GUTTER;
/**
 * Distance from the scroll container's content origin to t=0 — the control column's width.
 * Every viewport-x ↔ time conversion uses this, or the playhead and the bars disagree with
 * the ruler.
 */
export const TIMELINE_CONTENT_LEFT_INSET = TIMELINE_CONTROL_COLUMN_WIDTH;
/** Total ruler height (border-box); ticks and timestamps in timeline-ruler.module.scss position absolutely within it. */
export const RULER_HEIGHT = 24;
/**
 * The seek indicator's sticky top offset — must match $sticky-top-offset in seek-indicator.module.scss.
 * Its line is sized to (timelineHeight - this offset) so offset + height lands exactly on the scrollport's
 * bottom edge. Any overhang past that edge counts as scrollable overflow in Safari, where each overscroll
 * bounce then ratchets scrollHeight upward by the overhang, letting the tracks scroll away into blank space.
 */
export const SEEK_INDICATOR_STICKY_TOP_OFFSET = 0;
/** Small unlabeled tick lines drawn between each pair of labeled ruler timestamps, at every zoom level. */
export const MINOR_TICKS_BETWEEN_MAJOR_TICKS = 9;
export const TIMELINE_RIGHT_MARGIN = 64;
/** Pixel distance within which an item edge snaps to another item's edge */
export const SNAP_THRESHOLD_PX = 3;
/** Wider reach for a video bar's own loop landmarks (whole-pass, source-exhaust) — landing on a clean pass matters more than a stray edge */
export const OWN_LOOP_SNAP_THRESHOLD_PX = 5;
/** Total visual gap (px) opened between adjacent backgrounds during a reorder drag, sized to fit the indicator + 4px margin on each side */
export const BACKGROUND_DROP_INDICATOR_GAP_PX = 10;

/** Width of a background timeline item's own border — overlays inset by this to sit flush with the padding box. SCSS twin: $background-item-border-width in _timeline-z-tiers.scss — keep in sync. */
export const BACKGROUND_ITEM_BORDER_PX = 1;
